import React, { useEffect, useState } from 'react';

interface Props {
  label: string;                 // accessible name for the slider (not displayed)
  currentGoal?: number | null;
  onSet: (percent: number) => Promise<boolean> | boolean | void;  // truthy/void = sent OK
  disabled?: boolean;
}

const snap10 = (n: number) => Math.max(0, Math.min(100, Math.round(n / 10) * 10));

/** Charge-goal slider + GO button, mirroring the charger app: the button shows
 *  dark "GO!" while synced and flips to the green pending value the moment the
 *  user drags. A `dirty` flag stops the live-goal poll from yanking the slider
 *  mid-edit, and clears only after a successful send. Layout-agnostic — the
 *  caller owns the surrounding card. */
export default function GoalControlBar({ label, currentGoal, onSet, disabled }: Props) {
  const [pct, setPct] = useState<number>(currentGoal != null ? snap10(currentGoal) : 80);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  // Track the live goal — but never while the user is mid-edit or a send is in
  // flight (mirrors the charger app's "don't touch a dirty slider via SSE").
  useEffect(() => {
    if (currentGoal != null && !dirty && !busy) setPct(snap10(currentGoal));
  }, [currentGoal, dirty, busy]);

  const onSlide = (v: number) => { setPct(v); setDirty(true); };

  const handle = async () => {
    setBusy(true);
    try {
      const ok = await onSet(pct);
      if (ok !== false) setDirty(false);   // success → back to resting GO!
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <input
        type="range" min={0} max={100} step={10} value={pct}
        onChange={(e) => onSlide(parseInt(e.target.value, 10))}
        disabled={disabled || busy}
        aria-label={label}
        style={{ ['--value' as any]: `${pct}%` }}
        className="cc-goal-slider flex-1 min-w-0 m-0"
      />
      <button
        type="button" onClick={handle} disabled={disabled || busy}
        aria-busy={busy}
        aria-label={dirty ? `Apply ${pct} percent charge goal` : 'Re-apply charge goal'}
        className={`${dirty ? 'cc-go-btn' : 'cc-go-btn-idle'} px-4 py-2 min-w-[72px] text-center font-bold rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300`}
      >{busy ? '…' : dirty ? `${pct}%` : 'GO!'}</button>
    </div>
  );
}
